import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ChevronDownIcon,
  UserGroupIcon,
  CheckIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

const TeamSelector = ({ onTeamChange, onTeamSelect, onShowAll, allowAllOption = false, className = '' }) => {
  console.log('TeamSelector: Props received - onTeamChange:', typeof onTeamChange, 'onTeamSelect:', typeof onTeamSelect, 'onShowAll:', typeof onShowAll, 'allowAllOption:', allowAllOption);
  const { user, loading: authLoading } = useAuth();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastNotifiedTeamId = useRef(null);

  const notifyTeamChange = (team, force = false) => {
    console.log('TeamSelector: notifyTeamChange called with team:', team, 'force:', force);
    if (!team) {
      console.log('TeamSelector: notifyTeamChange - no team provided');
      return;
    }

    if (!force && lastNotifiedTeamId.current === team.id) {
      console.log('TeamSelector: notifyTeamChange - team already notified, skipping');
      return;
    }

    lastNotifiedTeamId.current = team.id;
    console.log('TeamSelector: notifyTeamChange - calling callbacks');

    if (onTeamChange) {
      console.log('TeamSelector: notifyTeamChange - calling onTeamChange');
      onTeamChange(team);
    }
    if (onTeamSelect) {
      console.log('TeamSelector: notifyTeamChange - calling onTeamSelect');
      onTeamSelect(team);
    }
  };

  // Fetch user's teams. isCurrent() is false once the user has changed, so a slow
  // response for a previous session can't overwrite the current team list.
  const fetchUserTeams = async (isCurrent) => {
    try {
      setLoading(true);
      console.log('TeamSelector: fetchUserTeams called, user:', user);

      if (!user || !user.token) {
        console.log('TeamSelector: No signed-in user, no teams to load');
        setTeams([]);
        return;
      }

      // Fetch user's actual teams from API
      const response = await axios.get('/api/teams/my-teams', {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      console.log('TeamSelector: Fetched user teams:', response.data);
      let combinedTeams = Array.isArray(response.data) ? [...response.data] : [];
      
      // For org admins, also fetch teams from their organizations
      const hasOrgPrivileges = ['admin', 'org_admin', 'app_manager'].includes(user?.role);
      if (hasOrgPrivileges) {
        try {
          // Fetch user's organizations
          const orgsResponse = await axios.get('/api/organizations/my-organizations', {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          });
          
          if (orgsResponse.data && Array.isArray(orgsResponse.data)) {
            const existingTeamIds = new Set(combinedTeams.map(team => team.id));
            
            // Fetch teams for each organization
            for (const org of orgsResponse.data) {
              const orgId = org?.Organization?.id ?? org?.organization_id ?? org?.id;
              if (!orgId) continue;
              
              try {
                const orgTeamsResponse = await axios.get('/api/teams', {
                  params: { organizationId: orgId },
                  headers: {
                    'Authorization': `Bearer ${user.token}`
                  }
                });
                
                const orgTeams = Array.isArray(orgTeamsResponse.data) ? orgTeamsResponse.data : [];
                orgTeams.forEach(team => {
                  if (!existingTeamIds.has(team.id)) {
                    // Add teams from organization that user isn't a member of
                    combinedTeams.push(team);
                    existingTeamIds.add(team.id);
                  }
                });
              } catch (orgError) {
                console.error(`Error fetching teams for organization ${org.id}:`, orgError);
              }
            }
          }
        } catch (orgError) {
          console.error('Error fetching organizations:', orgError);
          // Continue with just user teams if org fetch fails
        }
      }
      
      if (!isCurrent()) return;
      setTeams(combinedTeams);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching user teams:', error);
      toast.error('Failed to load teams');
      // Fallback to empty teams array
      setTeams([]);
      setSelectedTeam(null);
      notifyTeamChange(null);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  // Wait for AuthContext to restore the session; on first render user is still null,
  // which used to lock the selector into demo teams until a hard refresh.
  useEffect(() => {
    if (authLoading) return undefined;
    let current = true;
    fetchUserTeams(() => current);
    return () => { current = false; };
  }, [authLoading, user?.id, user?.token]);

  const handleTeamSelect = (team) => {
    setSelectedTeam(team);
    setIsOpen(false);
    notifyTeamChange(team, true);
  };

  const setPrimaryTeam = async (teamId) => {
    try {
      if (!user || !user.token) {
        // Demo mode - just update local state
        setTeams(prev => prev.map(t => ({
          ...t,
          is_primary: t.id === teamId
        })));
        return;
      }

      await axios.post(`/api/user-teams/user/${user.id}/primary`, {
        teamId
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      // Update local state
      setTeams(prev => prev.map(t => ({
        ...t,
        is_primary: t.id === teamId
      })));

      lastNotifiedTeamId.current = null;
      toast.success('Primary team updated');
    } catch (error) {
      console.error('Error setting primary team:', error);
      toast.error('Failed to update primary team');
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const PROGRAM_GRACE_DAYS = 30;
  const isAdmin = ['admin', 'org_admin', 'app_manager'].includes(user?.role);

  // Determine program-based visibility for a team.
  // Teams with no program are always visible.
  // Teams whose program has ended past the grace window are hidden for non-admins.
  // Teams whose program hasn't started yet are "staged" — admins see them, others don't.
  const getProgramStatus = (team) => {
    if (!team.program) return 'active';
    if (!team.program.is_active) return 'archived';
    if (team.program.start_date > today) return 'staged';
    if (team.program.end_date) {
      const graceEnd = new Date(new Date(team.program.end_date).getTime() + PROGRAM_GRACE_DAYS * 86400000)
        .toISOString().split('T')[0];
      if (today > graceEnd) return 'ended';
    }
    return 'active';
  };

  const visibleTeams = teams.filter(team => {
    const status = getProgramStatus(team);
    if (status === 'active') return true;
    if (status === 'staged') return isAdmin;
    if (status === 'archived' || status === 'ended') return isAdmin;
    return true;
  });

  console.log('TeamSelector render - loading:', loading, 'teams:', teams, 'selectedTeam:', selectedTeam);

  if (loading) {
    console.log('TeamSelector: Showing loading state');
    return (
      <div className={`animate-pulse bg-blue-200 border-2 border-blue-300 rounded-lg h-10 w-48 ${className}`}>
        <span className="text-xs text-blue-600 font-bold">DEBUG: Loading teams...</span>
      </div>
    );
  }

  if (visibleTeams.length === 0) {
    console.log('TeamSelector: No teams found, showing empty state');
    return (
      <div className={`text-center py-2 px-4 bg-red-100 border-2 border-red-300 rounded-lg ${className}`}>
        <span className="text-sm text-red-600 font-bold">DEBUG: No teams found - teams.length = {teams.length}</span>
      </div>
    );
  }

  console.log('TeamSelector: Rendering successful state with teams:', teams.length);
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 bg-green-100 border-2 border-green-300 rounded-lg hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <div className="flex items-center space-x-2">
          <UserGroupIcon className="h-4 w-4 text-gray-500" />
          <span className="truncate">
            {selectedTeam ? selectedTeam.name : 'Select Team'}
          </span>
        </div>
        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-2">
            <div className="text-xs font-medium text-gray-500 px-3 py-2 uppercase tracking-wide">
              Your Teams
            </div>
            {allowAllOption && (
              <div
                className="flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-gray-50 border border-dashed border-gray-200 mb-2"
                onClick={() => {
                  if (onShowAll) {
                    console.log('TeamSelector: All teams clicked - invoking onShowAll');
                    onShowAll();
                  }
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <UserGroupIcon className="h-4 w-4 text-gray-500" />
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">All teams</div>
                    <div className="text-xs text-gray-500">Show items across all teams</div>
                  </div>
                </div>
              </div>
            )}
            
            {visibleTeams.map((team) => {
              const programStatus = getProgramStatus(team);
              const isEnded = programStatus === 'ended' || programStatus === 'archived';
              return (
                <div
                  key={team.id}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-gray-50 ${
                    selectedTeam?.id === team.id ? 'bg-primary-50 border border-primary-200' : ''
                  } ${isEnded ? 'opacity-60' : ''}`}
                  onClick={() => handleTeamSelect(team)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                        <UserGroupIcon className="h-4 w-4 text-primary-600" />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900">{team.name}</span>
                        {programStatus === 'staged' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 font-medium">Staged</span>
                        )}
                        {isEnded && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">Ended</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {team.sport} • {team.ageGroup}
                        {team.program && <span className="ml-1">· {team.program.name}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {selectedTeam?.id === team.id && (
                      <CheckIcon className="h-4 w-4 text-primary-600" />
                    )}
                  </div>
                </div>
              );
            })}
            
            <div className="border-t border-gray-200 mt-2 pt-2">
              <button
                className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
                onClick={() => {
                  // TODO: Implement add team functionality
                  toast.info('Add team functionality coming soon!');
                }}
              >
                <PlusIcon className="h-4 w-4" />
                <span>Add New Team</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSelector;
